import { useMemo, useState } from 'react';
import { useCategories, usePoses } from '../data/poses';
import { useAuth } from '../auth/AuthContext';
import type { ProgressApi } from '../hooks/useProgress';
import { PoseCard } from './PoseCard';

interface Props {
  progress: ProgressApi;
}

type StatusFilter = 'all' | 'new' | 'practiced' | 'learned';

export function CatalogPage({ progress }: Props) {
  const poses = usePoses();
  const categories = useCategories();
  const { user } = useAuth();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return poses.filter((p) => {
      if (category && p.category !== category) return false;
      if (statusFilter !== 'all' && progress.statusOf(p.id) !== statusFilter) return false;
      if (!q) return true;
      const hay = [p.nameEnglish, p.nameSanskrit, p.category, p.description, p.benefits.join(' ')]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [query, category, statusFilter, progress]);

  return (
    <>
      <div className="controls">
        <label className="search">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.6" />
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            placeholder="Search poses, Sanskrit, benefits…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
        </label>
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
        >
          <option value="all">All statuses</option>
          <option value="new">Not started</option>
          <option value="practiced">Practiced</option>
          <option value="learned">Learned</option>
        </select>
        <span className="count">
          {filtered.length} of {poses.length}
        </span>
        {user?.isAdmin && (
          <a className="btn primary new-pose-btn" href="#/admin/new-pose">
            + New pose
          </a>
        )}
      </div>

      {filtered.length === 0 ? (
        <p className="empty">No poses match your filters.</p>
      ) : (
        <div className="grid">
          {filtered.map((p) => (
            <PoseCard key={p.id} pose={p} status={progress.statusOf(p.id)} />
          ))}
        </div>
      )}
    </>
  );
}
